import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import { ParallaxHeroImage } from "../components/ParallaxImage";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import AnswerLead from "../components/AnswerLead";
import PopstyleShowcase from "../components/PopstyleShowcase";
import { AREA_META } from "../lib/images";
import { AREA_EXTRA, VISIT_LABEL, PARTNER_LABEL } from "../i18n/areaExtra";
import { getArticles } from "../lib/api";

const AreaPage = () => {
  const { pathname } = useLocation();
  const area = pathname.replace(/^\//, "");
  const { t, lang } = useLang();
  const meta = AREA_META[area];
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (area === "interior") getArticles("interior").then(setRelated).catch(() => {});
    else setRelated([]);
  }, [area]);

  if (!meta) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-32">
        <p className="font-serif text-3xl text-white/60">{t.common.notFound}</p>
      </div>
    );
  }

  const info = t.areas[meta.key];
  const content = t.areaContent[meta.key];
  const extra = AREA_EXTRA[area];

  return (
    <div data-testid={`area-page-${area}`}>
      <Seo pageKey={area} path={`/${area}`} />
      {/* Hero */}
      <section className="relative flex min-h-[72vh] items-end overflow-hidden pt-24">
        <ParallaxHeroImage src={meta.hero} alt={info.name}>
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-obsidian/40" />
        </ParallaxHeroImage>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-20 md:px-12">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="text-xs uppercase tracking-[0.3em] text-champagne">
            {info.tag}
          </motion.span>
          <h1 className="mt-6 font-serif text-6xl font-light leading-[0.95] tracking-tight text-white md:text-8xl">
            <MaskedLines lines={[info.name]} startDelay={0.3} />
          </h1>
        </div>
      </section>

      <AnswerLead pageKey={area} />

      {/* Intro */}
      <section className="border-t border-white/10 bg-obsidian py-24 md:py-36">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-7">
            <Reveal>
              <p className="font-serif text-2xl font-light leading-relaxed text-white md:text-4xl">{content.intro}</p>
            </Reveal>
            {extra?.story && (
              <Reveal delay={0.1}>
                <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">{extra.story[lang]}</p>
              </Reveal>
            )}
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.15}>
              <h3 className="mb-8 text-xs uppercase tracking-[0.3em] text-champagne">{t.section.highlights}</h3>
              <ul className="space-y-5">
                {content.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-4 border-b border-white/10 pb-5">
                    <Check size={16} className="mt-1 shrink-0 text-champagne" />
                    <span className="text-base font-light text-white/75">{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Editorial image */}
      <section className="bg-obsidian-soft py-8 md:py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="relative aspect-[16/9] overflow-hidden border border-white/10">
              <img src={meta.image} alt={info.name} className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Partner world card (POPSTYLE gets a dedicated showcase on fashion) */}
      {area === "fashion" && <PopstyleShowcase />}
      {extra?.external && area !== "fashion" && (
        <section className="border-t border-white/10 bg-obsidian py-24 md:py-32" data-testid="area-partner-section">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.3em] text-champagne">{PARTNER_LABEL[lang]}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href={extra.external.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="area-external-link"
                className="group mt-10 grid overflow-hidden border border-white/10 bg-obsidian-soft md:grid-cols-2"
              >
                <div className="relative h-64 overflow-hidden md:h-auto">
                  <img src={meta.image} alt={extra.external.name} className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian-soft/70 to-transparent md:bg-gradient-to-r" />
                </div>
                <div className="flex flex-col justify-center p-8 md:p-12">
                  <h3 className="font-serif text-4xl font-light text-white transition-colors group-hover:text-champagne md:text-5xl">{extra.external.name}</h3>
                  <p className="mt-5 text-base font-light leading-relaxed text-white/60 md:text-lg">{extra.cardDesc[lang]}</p>
                  <span className="mt-8 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-champagne">
                    {VISIT_LABEL[lang]}
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </a>
            </Reveal>
          </div>
        </section>
      )}

      {/* Related articles for interior */}
      {related.length > 0 && (
        <section className="bg-obsidian-soft py-24">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <h3 className="mb-10 font-serif text-3xl font-light text-white">{t.library.more}</h3>
            <div className="grid gap-8 md:grid-cols-3">
              {related.slice(0, 3).map((a) => (
                <Link key={a.id} to={`/library/${a.slug}`} className="group block">
                  <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                    <img src={a.cover_image} alt={a.title[lang]} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  </div>
                  <h4 className="mt-4 font-serif text-xl font-light text-white group-hover:text-champagne transition-colors">{a.title[lang]}</h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FaqSection pageKey={area} />

      {/* CTA */}
      <section className="border-t border-white/10 bg-obsidian py-28 text-center md:py-40">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-4xl font-light leading-tight md:text-6xl">{t.section.talk}</h2>
            <p className="mx-auto mt-6 max-w-xl text-base font-light text-white/60 md:text-lg">{t.section.talkSub}</p>
            <Link
              to="/contact"
              data-testid="area-cta-contact"
              className="group mt-10 inline-flex items-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light"
            >
              {t.nav.book}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AreaPage;
